import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import useDashboardConfigStore from '../stores/useDashboardConfigStore';
import { MODULE_REGISTRY, DASHBOARD_DATA_MODULES, getOrderedModules, userCanSeeModule } from './moduleRegistry';
import DashboardModule from './DashboardModule';
import EditModeToolbar from './EditModeToolbar';
import Marquee from '../HomeScreen/Marquee';
import GoldStarDisplay from '../Stars/GoldStarDisplay';
import WelcomeSecret from '../fun/PilotSimulator/WelcomeSecret';
import config from '../config';
import PageLayout from '../components/ui/PageLayout';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel from '../components/ui/Panel';
import Alert from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

/* ------------------------------------------------------------------ */
/*  Available Modules Panel — shows hidden modules with Add buttons    */
/* ------------------------------------------------------------------ */
const AvailableModulesPanel = ({ hiddenModules, onAdd }) => {
  if (hiddenModules.length === 0) return null;

  return (
    <Panel
      title="Available Modules"
      level={3}
      actions={<span className="text-xs text-on-surface-variant tabular-nums">{hiddenModules.length} hidden</span>}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {hiddenModules.map(mod => {
          const meta = MODULE_REGISTRY[mod.id];
          if (!meta) return null;

          return (
            <button
              key={mod.id}
              onClick={() => onAdd(mod.id)}
              className="group flex items-center gap-3 p-3 rounded-md
                         bg-gray-900/60 border border-gray-700/30
                         hover:border-primary/30 hover:bg-gray-900/80
                         transition-all duration-200 text-left"
            >
              <div className="flex-shrink-0 w-7 h-7 rounded-md
                              bg-surface-container-high border border-transparent
                              group-hover:bg-primary/10 group-hover:border-primary/30
                              flex items-center justify-center
                              transition-all duration-200">
                <svg className="w-3.5 h-3.5 text-on-surface-variant group-hover:text-primary transition-colors duration-200"
                     fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-on-surface group-hover:text-white
                                transition-colors duration-200 truncate">
                  {meta.label}
                </div>
                <div className="text-[10px] text-on-surface-variant group-hover:text-on-surface
                                transition-colors duration-200 truncate">
                  {meta.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Panel>
  );
};

/* ------------------------------------------------------------------ */
/*  Unified Home Page / Dashboard Orchestrator                         */
/* ------------------------------------------------------------------ */
const FleetDashboard = () => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((state) => state.user);

  // Dashboard data (only fetched when role-gated modules are visible)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mobile menu

  // Dashboard config — use individual selectors to avoid full-store subscription
  const dashConfig = useDashboardConfigStore((s) => s.config);
  const savedConfig = useDashboardConfigStore((s) => s.savedConfig);
  const editMode = useDashboardConfigStore((s) => s.editMode);
  const configLoading = useDashboardConfigStore((s) => s.loading);
  const setEditMode = useDashboardConfigStore((s) => s.setEditMode);
  const reorderModules = useDashboardConfigStore((s) => s.reorderModules);
  const toggleModule = useDashboardConfigStore((s) => s.toggleModule);
  const saveConfig = useDashboardConfigStore((s) => s.saveConfig);
  const resetToDefaults = useDashboardConfigStore((s) => s.resetToDefaults);
  const fetchConfig = useDashboardConfigStore((s) => s.fetchConfig);

  const [saving, setSaving] = useState(false);

  // DnD sensors — 8px touch tolerance for field use
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  // Compute module lists (role-filtered)
  const allModules = getOrderedModules(dashConfig).filter(m => userCanSeeModule(m.id, user));
  const visibleModules = allModules.filter(m => m.visible);
  const hiddenModules = allModules.filter(m => !m.visible);
  const visibleIds = visibleModules.map(m => m.id);

  // Base fetch decision on saved config, not in-progress edits
  const needsDashboardData = getOrderedModules(savedConfig)
    .filter(m => m.visible && userCanSeeModule(m.id, user))
    .some(m => DASHBOARD_DATA_MODULES.has(m.id));

  // fetchDashboard always fetches — the guard lives in the effect, not the function.
  // This keeps onRefresh (passed to child modules) working correctly.
  const fetchDashboard = async () => {
    try {
      const result = await api.get('/dashboard');
      setData(result);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (!configLoading) {
      if (needsDashboardData) {
        fetchDashboard();
      } else {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading, needsDashboardData]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const allMods = getOrderedModules(dashConfig);
    const realOld = allMods.findIndex(m => m.id === active.id);
    const realNew = allMods.findIndex(m => m.id === over.id);
    if (realOld !== -1 && realNew !== -1) {
      reorderModules(realOld, realNew);
    }
  };

  const handleRemove = (moduleId) => toggleModule(moduleId);
  const handleAdd = (moduleId) => toggleModule(moduleId);
  const toggleSize = useDashboardConfigStore((s) => s.toggleSize);
  const handleToggleSize = (moduleId) => toggleSize(moduleId);

  const handleSave = async () => {
    setSaving(true);
    const ok = await saveConfig();
    setSaving(false);
    if (ok) {
      toast.success('Dashboard layout saved');
    } else {
      toast.error('Failed to save layout');
    }
  };

  // Props for modules that need dashboard API data
  const fleetCounts = data?.fleetCounts || {};
  const buildCapacity = data?.buildCapacity || {};
  const adjustments = data?.adjustments || [];
  const internalFleet = fleetCounts.internalFleet;
  const available = internalFleet != null ? internalFleet - (fleetCounts.totalLeased || 0) - (fleetCounts.totalRentalCommitted || 0) - (fleetCounts.activeFlagCount || 0) : null;

  const moduleProps = {
    fleetCounts,
    buildCapacity,
    adjustments,
    available,
    onRefresh: fetchDashboard,
  };

  // Show loading only if we're waiting for dashboard data that's actually needed
  if (loading && needsDashboardData) return <LogoSpinner label="Loading the dashboard" />;

  return (
    <>
    {/* Mobile nav is now global — provided by NavBar's mega-menu drawer */}

    <PageLayout kind="workspace" className="pt-6">
    <div className={`space-y-4 ${editMode ? 'pb-12' : ''}`}>

      {/* ---- Permanent Fixtures ------------------------------------ */}
      {/* The name is the Pilot Simulator front door (WelcomeSecret): it keeps the
          .username font and shine, and renders inert on Swarm. Tap it to find out. */}
      <h3 className="text-center text-xl mb-2 font-mono">
        Welcome <WelcomeSecret name={user.firstname} />!
      </h3>

      {config.API_ENDPOINT.includes('localhost') && (
        <Alert tone="warning" live={false} icon={false} className="text-center font-bold uppercase [&_div]:text-amber-200">
          Using the development database
        </Alert>
      )}

      <GoldStarDisplay />
      <Marquee />

      {/* ---- Edit mode header --------------------------------------- */}
      <Reveal show={editMode}>
        <div>
          <h2 className="text-lg font-bold text-white tracking-tight">Customize Dashboard</h2>
          <p className="text-[11px] text-on-surface-variant uppercase tracking-widest mt-0.5">
            Drag to reorder &middot; Remove or add modules
          </p>
        </div>
      </Reveal>

      {/* ---- Active Modules ---------------------------------------- */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={visibleIds} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleModules.map(mod => {
              const registry = MODULE_REGISTRY[mod.id];
              if (!registry) return null;
              const isHalf = (mod.size || 'full') === 'half';

              return (
                <div key={mod.id} className={`${isHalf ? 'md:col-span-1' : 'md:col-span-2'} min-h-0`}>
                  <DashboardModule
                    moduleConfig={mod}
                    editMode={editMode}
                    onRemove={handleRemove}
                    onToggleSize={handleToggleSize}
                  >
                    {registry.render(moduleProps)}
                  </DashboardModule>
                </div>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {/* ---- Available (hidden) modules panel ----------------------- */}
      <Reveal show={editMode && hiddenModules.length > 0}>
        <AvailableModulesPanel
          hiddenModules={hiddenModules}
          onAdd={handleAdd}
        />
      </Reveal>

      {/* ---- Edit mode toolbar -------------------------------------- */}
      {editMode && (
        <EditModeToolbar
          onSave={handleSave}
          onCancel={() => setEditMode(false)}
          onReset={resetToDefaults}
          saving={saving}
        />
      )}

    </div>
    </PageLayout>
    </>
  );
};

export default FleetDashboard;
